import { Container, Title, Text, Paper, Group, Stack, Badge, Divider, List, ThemeIcon, rem } from '@mantine/core';
import { IconMountain, IconBook, IconLanguage, IconWorld } from '@tabler/icons-react';

interface Experience {
    title: string;
    company: string;
    location: string;
    period: string;
    responsibilities: string[];
    technologies?: string[];
}

interface Education {
    school: string;
    location: string;
    degree: string;
    period: string;
}

interface Project {
    name: string;
    role: string;
    period: string;
    description: string;
    link?: string;
}

const experiences: Experience[] = [
    {
        title: "Director - FICC Technology",
        company: "TD Securities",
        location: "Toronto, ON",
        period: "Jan 2024 - Present",
        responsibilities: [
            "Oversees large portions of Commodities Technology, with a growing portfolio of Interop-enabled trading apps covering Trade Automation, Murex-integrated Orderbooks, and an overhauled Inventory Management system",
            "Manages a cross-functional team of up to 20 engineers, product designers and analysts across multiple regions, including Toronto, Calgary, NYC and London",
            "Owns and leads adoption of internal Desktop Interop platform (Built with Glue42/Interop.io), housing over 100 applications across multiple business lines",
            "Launched a new line of Atomic branded products - React MicroUIs which can be customized by Traders inside of Interop-powered Workspaces",
            "Regularly speaks at conferences and participates in webinars"
        ],
        technologies: ["React", "Typescript", "Next.js", "Interop.io", "NodeJS", "Java", "MongoDB", "MS SQL", "Murex & Murex Saas", "Rest APIs", "Solace"]
    },
    {
        title: "VP - App Engineering & Design",
        company: "TD Securities",
        location: "Toronto, ON",
        period: "2020 - 2023",
        responsibilities: [
            "Introduced Interoperability tech to TD Bank through strategic partnership with Glue42 (now Interop.io)",
            "Designed and developed industry-leading Desktop Interop platform - TDS Workspace (100+ apps, 50+ developers, 10+ business lines)",
            "Founded first TDS Technology Practice (The Factory) specializing in UI/UX Development & Design",
            "Grew practice to personally manage 20 developers building apps across many business lines",
            "2021 Pinnacle Award Winner - Awarded to the top ~3% of Employees"
        ],
        technologies: ["React", "Node", "Glue42", "Interop.io", "MongoDB", "Progressive Web Apps", "MicroUIs", "Workspaces", "Mantine", "Figma"]
    },
    {
        title: "Managing Engineer",
        company: "TD Securities",
        location: "Toronto, ON",
        period: "2017 - 2020",
        responsibilities: [
            "Managed devs to build web apps, microservices and common design framework within Cross Asset Platform team",
            "Created TDS App Platform to build and deploy web apps to cloud in under 5 mins with a few clicks",
            "Established an internal dev/design toolkit for rapid development of new apps",
            "Evangelized App Development across TDS via various presentations, town halls and large-scale demos"
        ],
        technologies: ["React", "Node", "Jest", "Enzyme", "MongoDB", "IndexedDB", "PWAs", "Sockets", "Sass Modules", "oAuth", "OpenFin", "Glue42", "GatsbyJs"]
    },
    {
        title: "Software Developer Lead",
        company: "TD Securities",
        location: "Toronto, ON",
        period: "2016 - 2017",
        responsibilities: [
            "Led a small team of devs to build a core Market & Reference Data app within Valuation Services team",
            "Responsible for app architecture, design, downstream integration, roadmapping & distribution of development tasks"
        ],
        technologies: ["AngularJs", "ReactJs", "Scala", "Node", "Bower", "Karma", "ZMQ", "SASS"]
    }
];

const education: Education[] = [
    {
        school: "Queen's University",
        location: "Kingston, ON",
        degree: "Bachelor of Computing - Honours",
        period: "2010 - 2014"
    },
    {
        school: "University of Toronto",
        location: "Toronto, ON",
        degree: "Machine Learning Course",
        period: "2019"
    }
];

const projects: Project[] = [
    {
        name: "Crow",
        role: "Founder & CEO",
        period: "2024 - Present",
        description: "Upcoming app (iOS, Android, Web) designed to help users turn their aspirations into achievements via a proprietary blend of simplified UI/UX, daily interaction and AI-powered insights. The current features aim to gamify each day with a score, then track progress and trends over time while competing with friends.",
        link: "https://crow.app"
    },
    {
        name: "Yety",
        role: "Founder & CEO",
        period: "2017 - 2018",
        description: "Successfully created and launched a cryptocurrency startup focused on investment bank level analytics (Aggregate PnL, Risk) for the common crypto trader. Users could connect their trading accounts from various exchanges and Yety would provide centralized analytics on their holdings, positions and trades in a beautiful user interface.",
        link: "https://yety.io"
    }
];

const hobbies = [
    { icon: IconMountain, text: "High-Altitude Mountaineering all around the world" },
    { icon: IconWorld, text: "Backpacking to new countries every year with friends" },
    { icon: IconBook, text: "Reading 15+ books per year (My favourites being Historical Fiction and Biographies)" },
    { icon: IconLanguage, text: "Learning Spanish (My Duolingo streak is unbreakable)" }
];

export default function Career() {
    return (
        <Container size="xl" py="xl">
            {/* Header */}
            <Stack gap="xs" mb="xl">
                <Title order={1}>Tom Herren</Title>
                <Text size="lg" c="dimmed">Engineering Director & Technology Leader</Text>
                <Group gap="md">
                    <Text>Toronto, ON</Text>
                    <Text>647-545-1992</Text>
                    <Text>tom.herren@outlook.com</Text>
                    <Text component="a" href="https://linkedin.com/in/tomherren" target="_blank" rel="noopener noreferrer">LinkedIn</Text>
                    <Text component="a" href="https://tomherren.com" target="_blank" rel="noopener noreferrer">Website</Text>
                </Group>
            </Stack>

            {/* Summary */}
            <Paper withBorder p="md" mb="xl">
                <Text>
                    Accomplished Engineering Director with 9 years of experience in leading global technology teams at a major
                    Investment Bank. Proven track record of designing and delivering innovative platforms, while collaborating
                    seamlessly with senior leadership and stakeholders across multiple business lines. Expertise in crafting teams of
                    talented, cross-functional individuals to deliver industry-leading products.
                </Text>
            </Paper>

            {/* Experience */}
            <Title order={2} mb="md">Professional Experience</Title>
            <Stack gap="xl">
                {experiences.map((exp, index) => (
                    <Paper key={index} withBorder p="md">
                        <Stack gap="xs">
                            <Group justify="space-between">
                                <div>
                                    <Text fw={500} size="lg">{exp.title}</Text>
                                    <Text c="dimmed">{exp.company} - {exp.location}</Text>
                                </div>
                                <Text c="dimmed">{exp.period}</Text>
                            </Group>
                            <List spacing="xs">
                                {exp.responsibilities.map((resp, i) => (
                                    <List.Item key={i}>{resp}</List.Item>
                                ))}
                            </List>
                            {exp.technologies && (
                                <>
                                    <Divider my="sm" />
                                    <Group gap="xs">
                                        {exp.technologies.map((tech, i) => (
                                            <Badge key={i} variant="light">{tech}</Badge>
                                        ))}
                                    </Group>
                                </>
                            )}
                        </Stack>
                    </Paper>
                ))}
            </Stack>

            {/* Projects */}
            <Title order={2} mt="xl" mb="md">Startups & Projects</Title>
            <Stack gap="xl">
                {projects.map((project, index) => (
                    <Paper key={index} withBorder p="md">
                        <Stack gap="xs">
                            <Group justify="space-between">
                                <div>
                                    <Text fw={500} size="lg">{project.name}</Text>
                                    <Text c="dimmed">{project.role}</Text>
                                </div>
                                <Text c="dimmed">{project.period}</Text>
                            </Group>
                            <Text>{project.description}</Text>
                            {project.link && (
                                <Text component="a" href={project.link} target="_blank" rel="noopener noreferrer">
                                    Visit Website
                                </Text>
                            )}
                        </Stack>
                    </Paper>
                ))}
            </Stack>

            {/* Education */}
            <Title order={2} mt="xl" mb="md">Education</Title>
            <Stack gap="md">
                {education.map((edu, index) => (
                    <Paper key={index} withBorder p="md">
                        <Group justify="space-between">
                            <div>
                                <Text fw={500}>{edu.school}</Text>
                                <Text c="dimmed">{edu.location}</Text>
                                <Text>{edu.degree}</Text>
                            </div>
                            <Text c="dimmed">{edu.period}</Text>
                        </Group>
                    </Paper>
                ))}
            </Stack>

            {/* Hobbies */}
            <Title order={2} mt="xl" mb="md">Hobbies & Interests</Title>
            <Paper withBorder p="md">
                <Stack gap="md">
                    {hobbies.map((hobby, index) => (
                        <Group key={index} gap="sm">
                            <ThemeIcon size={34} variant="light">
                                <hobby.icon style={{ width: rem(20), height: rem(20) }} />
                            </ThemeIcon>
                            <Text>{hobby.text}</Text>
                        </Group>
                    ))}
                </Stack>
            </Paper>
        </Container>
    );
} 